import { useCallback, useEffect, useMemo, useState } from 'react';
import '../backlog/backlog.css';
import '../modules/SettingsDetailPage.css';   // shared .sd-grid--side / .sd-input (AI Standard Form Layout)
import './dataPackageTestView.css';
import { api } from '../../api/client';
import { AppDataGrid, type AppColumn, gridExportMatrix } from '../../components/AppDataGrid';
import { AppDialog } from '../../components/AppDialog';
import { AppToolbar, ExportMenu } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { GridSearch } from '../../components/GridSearch';
import { useNotify } from '../../components/notifications';
import { useSaveFlow } from '../../components/confirm';
import { LookupCombo } from '../../components/LookupCombo';
import type { TestMethodOption } from '../modules/itemTestsModel';

// ─────────────────────────────────────────────────────────────────────────────
// Settings ▸ Data Package ▸ Data Package Test View  (read-only)
//
// Port of the legacy ALPACA XAF "Data Package Test View"
// (TestMethodCheckList_DetailView over DataPackageCheckList, opened as a popup by
// DataPackageViewController.TestView_Execute). You pick a Test (TestMethod grid-
// lookup — legacy TestMethod_LookupListView_CMDP: Matrix · Test · Method#) and the
// form shows that test's Department and its "Check Lists" grid — the
// DataPackageCheckList rows whose semicolon-joined TestMethodOid contains the test
// (legacy TestMethod.DataPackageCheckListByTest), columns CheckPointID · CheckPoint ·
// QCRole · QCRootRole · Default · Sort. Entirely read-only (no New/Edit/Delete) —
// the master list is Settings ▸ Data Package ▸ Data Package Review Checklist.
//
// Built from the shared components (FormTitle / AppToolbar / AppDataGrid) + the
// shared TestMethodPickerDialog, matching the legacy layout.
// ─────────────────────────────────────────────────────────────────────────────

/** One Test lookup row as the API serialises DataPackageTestLookupRow. */
interface TestLookupApiRow {
  id: string; matrix: string | null; test: string; methodNumber: string | null; methodName: string | null;
}

/** One Check Lists row as the API serialises DataPackageTestCheckRow. */
interface CheckRow {
  oid: string; checkPointId: string | null; checkPoint: string; qcRole: string | null;
  qcRootRole: string | null; defaultOption: string | null; sort: number;
}

interface ByTestApi { departmentId: string | null; departmentName: string | null; rows: CheckRow[]; }

/** A Department option as the API serialises DataPackageChecklistOption. */
interface DeptOption { id: string; name: string; }

/** The clicked check point's detail (GET …/{id}) — the popup header fields. */
interface CheckDetail {
  checkPointId: string | null; checkPoint: string; departmentNames: string | null;
  qcRolesName: string | null; qcRootRolesName: string | null;
  defaultOption: string | null; sort: number | null; comment: string | null;
  // Oids (DataPackageChecklistDetail) needed to re-save the check point when its
  // test-method assignment is edited in this popup.
  departments: string[]; tests: string[]; qcRoles: string | null; qcRootRoles: string | null;
}

/** One "Test Methods" tab row (GET …/{id}/test-methods — DataPackageTestMethodRow). */
interface TmRow {
  id: string; name: string; methodName: string | null; matrix: string | null;
  formulaCount: number; isFormulaActive: boolean;
}

export default function DataPackageTestViewPage() {
  const notify = useNotify();

  const [tests, setTests] = useState<TestMethodOption[]>([]);
  const [testsLoading, setTestsLoading] = useState(true);

  const [selTest, setSelTest] = useState<TestMethodOption | null>(null);
  // Department is a dropdown (legacy TestMethod.Departmentname FK): N/A + every
  // dbo.Department, defaulting to the selected test's department. '' = N/A.
  const [departments, setDepartments] = useState<DeptOption[]>([]);
  const [deptId, setDeptId] = useState('');
  const [rows, setRows] = useState<CheckRow[]>([]);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [loadMs, setLoadMs] = useState<number | null>(null);

  // Check Lists search + Maximize (owner request 2026-10-15 — match the Data Package
  // Review Checklist search-row layout: a Search box left, Export + Maximize right, on
  // ONE row above the grid card, with the grid's own header tools suppressed). Both are
  // the shared pieces (GridSearch / ExportMenu / gridExportMatrix / .adg-max-btn), the
  // same CityPage / Field Tests pattern. Maximize is PAGE-LEVEL (hide the chrome so the
  // card fills) rather than the grid's own position:fixed, so the search row — and its
  // Restore button — stays visible and clickable.
  const [checkSearch, setCheckSearch] = useState('');
  const [maximized, setMaximized] = useState(false);
  useEffect(() => {
    if (!maximized) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMaximized(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [maximized]);

  // ── Load the Department dropdown options once ──
  useEffect(() => {
    void (async () => {
      try {
        const res = await api.get('/api/v1/data-package-checklists/departments');
        setDepartments((res.data?.data ?? []) as DeptOption[]);
      } catch {
        notify.error('Could not load the department list.');
        setDepartments([]);
      }
    })();
  }, [notify]);

  // ── Load the Test grid-lookup options once ──
  const loadTests = useCallback(async () => {
    setTestsLoading(true);
    const t0 = performance.now();
    try {
      const res = await api.get('/api/v1/data-package-checklists/test-lookup');
      const data = (res.data?.data ?? []) as TestLookupApiRow[];
      setTests(data.map((r) => ({
        id: r.id,
        matrix: r.matrix ?? '',
        test: r.test ?? '',
        methodNumber: r.methodNumber ?? '',
        methodName: r.methodName ?? '',
      })));
      setLoadMs(Math.round(performance.now() - t0));
    } catch {
      notify.error('Could not load the test list.');
      setTests([]);
    } finally {
      setTestsLoading(false);
    }
  }, [notify]);
  useEffect(() => { void loadTests(); }, [loadTests]);

  // ── Selecting a Test → load its Department + Check Lists ──
  const loadByTest = useCallback(async (testId: string) => {
    setRowsLoading(true);
    try {
      const res = await api.get(`/api/v1/data-package-checklists/by-test/${testId}`);
      const data = (res.data?.data ?? { departmentId: null, departmentName: null, rows: [] }) as ByTestApi;
      setDeptId(data.departmentId ?? '');
      setRows(data.rows ?? []);
    } catch {
      notify.error('Could not load the check lists for this test.');
      setDeptId('');
      setRows([]);
    } finally {
      setRowsLoading(false);
    }
  }, [notify]);

  const pickTest = (opt: TestMethodOption) => {
    setSelTest(opt);
    void loadByTest(opt.id);
  };

  // The Test grid-lookup rows — Matrix · Test · Method (legacy column order). On
  // pick the full row is matched back to its TestMethodOption to recover the id.
  const testLookup = useMemo(
    () => ({ cols: ['Matrix', 'Test', 'Method'], rows: tests.map((t) => [t.matrix, t.test, t.methodNumber]) }),
    [tests],
  );
  const onPickTest = (row: string[]) => {
    const picked = tests.find((t) => t.matrix === row[0] && t.test === row[1] && t.methodNumber === row[2]);
    if (picked) pickTest(picked);
  };

  const refresh = () => {
    void loadTests();
    if (selTest) void loadByTest(selTest.id);
  };

  // ── Check-point detail popup (legacy DataPackageCheckList_DetailView) ──
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState<CheckDetail | null>(null);
  const [tms, setTms] = useState<TmRow[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  // Editable assignment (owner request 2026-10-12): the opened check point + the
  // ticked (= assigned) test methods — the grid's first-column checkboxes, pre-ticked
  // with the current live set; OK saves via the shared flow.
  const [detailRow, setDetailRow] = useState<CheckRow | null>(null);
  const [kept, setKept] = useState<Set<string | number>>(new Set());
  const runSave = useSaveFlow();

  const openDetail = useCallback(async (row: CheckRow) => {
    setDetailOpen(true);
    setDetailRow(row);
    setDetail(null);
    setTms([]);
    setKept(new Set());
    setDetailLoading(true);
    try {
      const [dRes, tRes] = await Promise.all([
        api.get(`/api/v1/data-package-checklists/${row.oid}`),
        api.get(`/api/v1/data-package-checklists/${row.oid}/test-methods`),
      ]);
      setDetail((dRes.data?.data ?? null) as CheckDetail | null);
      const tmRows = (tRes.data?.data ?? []) as TmRow[];
      setTms(tmRows);
      // Every shown (live, assigned) test method starts ticked.
      setKept(new Set(tmRows.map((r) => r.id)));
    } catch {
      notify.error('Could not load the check point detail.');
    } finally {
      setDetailLoading(false);
    }
  }, [notify]);

  // OK — save the ticked test methods as the check point's assignment. Retired
  // (hidden) test methods already in the stored list are PRESERVED so editing the
  // live ones never silently drops them. Shared save flow (confirm → audited PUT →
  // "Saved successfully." → refresh the current test's check lists).
  const saveAssignment = () => {
    const row = detailRow, d = detail;
    if (!row || !d) return;
    const liveIds = new Set(tms.map((t) => t.id.toLowerCase()));
    const retired = d.tests.filter((id) => !liveIds.has(id.toLowerCase()));
    const tests = [...[...kept].map(String), ...retired];
    void runSave({
      formName: 'Data Package Review Checklist',
      auditAction: 'edit',
      save: async () => {
        await api.put(`/api/v1/data-package-checklists/${row.oid}`, {
          checkPoint: d.checkPoint, departments: d.departments, tests,
          qcRoles: d.qcRoles, qcRootRoles: d.qcRootRoles,
          defaultOption: d.defaultOption, sort: d.sort, comment: d.comment,
        });
      },
      onSaved: () => { setDetailOpen(false); if (selTest) void loadByTest(selTest.id); },
      onError: () => notify.error('Could not save the test method assignment.'),
    });
  };

  const tmColumns: AppColumn<TmRow>[] = useMemo(() => [
    { field: 'name', header: 'TestName', width: 240 },
    { field: 'methodName', header: 'MethodName', width: 260, render: (r) => r.methodName ?? '', text: (r) => r.methodName ?? '' },
    { field: 'formulaCount', header: 'FormulaCount', width: 120, align: 'center', compare: (a, b) => a.formulaCount - b.formulaCount },
    {
      field: 'isFormulaActive', header: 'IsFormulaActive', width: 130, align: 'center',
      render: (r) => <input type="checkbox" checked={r.isFormulaActive} readOnly disabled />,
      text: (r) => (r.isFormulaActive ? 'Yes' : ''),
    },
  ], []);

  const columns: AppColumn<CheckRow>[] = useMemo(() => [
    { field: 'checkPointId', header: 'CheckPointID', width: 120 },
    { field: 'checkPoint', header: 'CheckPoint', width: 440 },
    { field: 'qcRole', header: 'QCRole', width: 150, render: (r) => r.qcRole ?? '', text: (r) => r.qcRole ?? '' },
    { field: 'qcRootRole', header: 'QCRootRole', width: 150, render: (r) => r.qcRootRole ?? '', text: (r) => r.qcRootRole ?? '' },
    { field: 'defaultOption', header: 'Default', width: 80, align: 'center', render: (r) => r.defaultOption ?? '', text: (r) => r.defaultOption ?? '' },
    { field: 'sort', header: 'Sort', width: 70, align: 'center', compare: (a, b) => a.sort - b.sort },
  ], []);

  // Client-side Check Lists filter (substring across every visible column), matching
  // the shared list-search behaviour. Also feeds the Export so it is WYSIWYG.
  const filteredRows = useMemo(() => {
    const q = checkSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.checkPointId, r.checkPoint, r.qcRole, r.qcRootRole, r.defaultOption, String(r.sort)]
        .some((v) => (v ?? '').toLowerCase().includes(q)),
    );
  }, [rows, checkSearch]);

  return (
    <div className="bl-page">
      {!maximized && (
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span>
            <i className="ti ti-chevron-right" />
            <span>Settings</span>
            <i className="ti ti-chevron-right" />
            <span>Data Package</span>
            <i className="ti ti-chevron-right" />
            <span className="dp-bc-active">Data Package Test View</span>
          </div>
          <div className="dp-title-row">
            {/* Read-only report — no mutations, so no audit log to surface
                (standing rule 11: audit shown only when audit-trail data exists). */}
            <FormTitle pageId="set-datapackage-testview" loadMs={loadMs} loading={testsLoading} />
          </div>
          <div className="dp-subtitle">
            Read-only · {selTest ? `${rows.length} check point${rows.length === 1 ? '' : 's'}` : 'select a test'}
          </div>
        </div>
      </div>
      )}

      <div className="bl-grid-area">
        {!maximized && <AppToolbar onRefresh={refresh} refreshing={testsLoading || rowsLoading} />}

        {/* ── Test + Department header (legacy TestMethodCheckList_DetailView top row) ──
            AI Standard Form Layout (standing rule 12): shared .sd-grid--side side
            labels + .sd-input field styling (yellow editable / grey read-only, 26px,
            required *). Test is a grid-lookup trigger opening the shared picker. */}
        {!maximized && (
        <div className="sd-grid sd-grid--side dptv-head">
          <div className="sd-row">
            <label className="sd-label" htmlFor="dptv-test">Test<span className="req">*</span></label>
            {/* Legacy grid-lookup (ALPACA TestMethod_LookupListView_CMDP): an INLINE
                dropdown grid — Matrix · Test · Method with per-column filters, attached
                to the field — NOT a modal popup (owner report 2026-10-12, image 443). */}
            <LookupCombo
              id="dptv-test"
              lookup={testLookup}
              value={selTest ? selTest.test : ''}
              placeholder="Select a test…"
              onPick={onPickTest}
              columnFilters
              showFilter={false}
              colWidths={[110, 230, 230]}
              popupWidth={600}
              wrapCells
              className={selTest ? undefined : 'required-empty'}
            />
          </div>

          <div className="sd-row">
            <label className="sd-label" htmlFor="dptv-dept">Department</label>
            {/* Legacy TestMethod.Departmentname FK → editable dropdown of N/A + every
                department, defaulting to the test's department (legacy has no Save on
                this view, so the choice is informational — parity, not persisted). */}
            <select
              id="dptv-dept"
              className="sd-input"
              value={deptId}
              onChange={(e) => setDeptId(e.target.value)}
            >
              <option value="">N/A</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </div>
        </div>
        )}

        {/* Search row (owner request 2026-10-15) — matches the Data Package Review
            Checklist layout: Search on the LEFT, Export + Maximize on the RIGHT, one row
            above the grid card. Uses the shared GridSearch / ExportMenu / .adg-max-btn
            (CityPage / Field Tests pattern). The grid's own header tools are hidden
            (hideHeaderTools); Maximize is PAGE-LEVEL so this row — and its Restore
            button — stays visible. Export and the grid both read `filteredRows`, so the
            export is WYSIWYG with the search. */}
        <GridSearch
          value={checkSearch}
          onChange={setCheckSearch}
          placeholder="Search…"
          style={maximized ? { padding: '4px 0 7px' } : undefined}
          right={
            <div className="list-search-actions">
              <ExportMenu
                iconOnly
                triggerClassName="adg-max-btn"
                filename="Data-Package-Test-View"
                {...gridExportMatrix(filteredRows, columns)}
              />
              <button
                type="button"
                className="adg-max-btn"
                onClick={() => setMaximized((v) => !v)}
                title={maximized ? 'Restore' : 'Maximize'}
                aria-label={maximized ? 'Restore' : 'Maximize'}
              >
                <i className={`ti ti-arrows-${maximized ? 'minimize' : 'maximize'}`} />
              </button>
            </div>
          }
        />

        <div className="bl-card bl-card-flex">
          <AppDataGrid<CheckRow>
            rows={filteredRows}
            columns={columns}
            getRowId={(r) => r.oid}
            loading={rowsLoading}
            hideHeaderTools
            initialSort={{ key: 'sort', dir: 'asc' }}
            onRowClick={(r) => { void openDetail(r); }}
            emptyText={selTest ? 'No check lists for this test.' : 'Select a test to view its check lists.'}
          />
        </div>
      </div>

      {/* ── Check-point detail popup (legacy DataPackageCheckList_DetailView,
          opened by a Check Lists row click) — read-only header + "Test Methods" tab. ── */}
      <AppDialog
        open={detailOpen}
        title="Data Package Check List"
        width={860}
        className="dlg-center"
        maximizable
        onClose={() => setDetailOpen(false)}
        footer={
          <>
            <button type="button" className="ca-btn ca-btn-solid" onClick={saveAssignment}>OK</button>
            <button type="button" className="ca-btn" onClick={() => setDetailOpen(false)}>Cancel</button>
          </>
        }
      >
        <div className="dptv-detail">
          <dl className="dptv-dl">
            <dt>Check Point ID</dt><dd>{detail?.checkPointId ?? ''}</dd>
            <dt>Check Point</dt><dd>{detail?.checkPoint ?? ''}</dd>
            <dt>Department</dt><dd>{detail?.departmentNames || 'N/A'}</dd>
            <dt>Qc Roles</dt><dd>{detail?.qcRolesName || 'N/A'}</dd>
            <dt>Qc Root Roles</dt><dd>{detail?.qcRootRolesName || 'N/A'}</dd>
            <dt>Sort</dt><dd>{detail?.sort ?? ''}</dd>
            <dt>Default</dt><dd>{detail?.defaultOption || 'N/A'}</dd>
            <dt>Comment</dt><dd>{detail?.comment ?? ''}</dd>
          </dl>

          <div className="dptv-tm-h">Test Methods</div>
          <AppDataGrid<TmRow>
            rows={tms}
            columns={tmColumns}
            getRowId={(r) => r.id}
            loading={detailLoading}
            defaultPageSize={20}
            selectable
            selectedIds={kept}
            onSelectionChange={setKept}
            exportFilename="Data-Package-Check-List-Test-Methods"
            emptyText="No test methods"
          />
        </div>
      </AppDialog>
    </div>
  );
}
